import { useEffect, useRef, type MutableRefObject } from 'react';
import mapboxgl from 'mapbox-gl';

interface UseMapInteractionsOptions {
  mapRef: MutableRefObject<mapboxgl.Map | null>;
  lockZoom?: boolean;
  fixedZoomLevel?: number;
  freezeZoom?: boolean;
  disablePan?: boolean;
  disableUserZoom?: boolean;
  detailView?: boolean;
  zoom: number;
  losAngelesOnly: boolean;
  preceptorsLength: number;
}

export const useMapInteractions = ({
  mapRef,
  lockZoom,
  fixedZoomLevel,
  freezeZoom,
  disablePan,
  disableUserZoom,
  detailView,
  zoom,
  losAngelesOnly,
  preceptorsLength,
}: UseMapInteractionsOptions) => {
  const navAddedRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const initialZoom = losAngelesOnly ? 12 : zoom;
    const interactionLocked = lockZoom || freezeZoom;
    if (interactionLocked) {
      const z = fixedZoomLevel ?? initialZoom;
      map.setZoom(z);
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
      map.dragRotate?.disable();
      if (freezeZoom) {
        map.keyboard?.disable();
        if (disablePan) {
          map.dragPan?.disable();
        } else {
          map.dragPan?.enable();
        }
      }
    } else {
      if (!disableUserZoom && !navAddedRef.current) {
        map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
        navAddedRef.current = true;
      }
      if (disableUserZoom) {
        map.scrollZoom.disable();
        map.boxZoom.disable();
        map.doubleClickZoom.disable();
        map.touchZoomRotate.disable();
        map.dragRotate?.disable();
        if (!disablePan) map.dragPan?.enable(); else map.dragPan?.disable();
      }
    }

    if (detailView && !fixedZoomLevel) {
      if (map.getZoom() < 15) map.setZoom(15);
      if (map.getZoom() > 16) map.setZoom(16);
    }

    if (!losAngelesOnly && preceptorsLength === 1 && map.getZoom() < 15) {
      map.setZoom(15);
    }

    if (freezeZoom && !fixedZoomLevel) {
      const finalZ = map.getZoom();
      map.setMinZoom(finalZ);
      map.setMaxZoom(finalZ);
    } else if (freezeZoom && fixedZoomLevel) {
      map.setMinZoom(fixedZoomLevel);
      map.setMaxZoom(fixedZoomLevel);
    }
  }, [mapRef, lockZoom, fixedZoomLevel, freezeZoom, disablePan, disableUserZoom, detailView, zoom, losAngelesOnly, preceptorsLength]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || lockZoom || freezeZoom) return;
    if (disableUserZoom) {
      map.scrollZoom.disable();
      map.boxZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
      map.dragRotate?.disable();
      if (!disablePan) map.dragPan?.enable(); else map.dragPan?.disable();
    } else {
      map.scrollZoom.enable();
      map.boxZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoomRotate.enable();
      map.dragRotate?.enable();
      if (!disablePan) map.dragPan?.enable(); else map.dragPan?.disable();
    }
  }, [mapRef, disableUserZoom, disablePan, lockZoom, freezeZoom]);
};
